import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import {
  ActivityDetailData,
  ActivityNote,
  EvaluateRequestStatus,
  MentorRequestStatus,
  HistoricalActivity,
  addEvaluateNote,
  addMentorNote,
  closeMentorRequest,
  fetchActivityDetail,
  setEvaluateStatus,
} from "@/api/activities";
import { useMentorRequests } from "@/context/MentorRequestsContext";
import { useEvaluateRequests } from "@/context/EvaluateRequestsContext";
import { ActivityMessages } from "@/components/champion/ActivityRequests/ActivityMessages";
import { NoteDialog } from "@/components/champion/ActivityRequests/NoteDialog";

import S from "./RequestDetail.module.scss";

// ---------------------------------------------------------------------------
// Status config maps (module-level)
// ---------------------------------------------------------------------------

type SeverityType = "success" | "warning" | "danger" | "info";

interface StatusConfig {
  label: string;
  severity: SeverityType;
  icon: string;
}

const EVALUATE_STATUS_CONFIG: Record<EvaluateRequestStatus, StatusConfig> = {
  in_progress: { label: "In Progress", severity: "info", icon: "pi pi-clock" },
  submitted: { label: "Submitted", severity: "warning", icon: "pi pi-send" },
  failed: { label: "Failed", severity: "danger", icon: "pi pi-times-circle" },
  passed: { label: "Completed", severity: "success", icon: "pi pi-check-circle" },
  completed: { label: "Completed", severity: "success", icon: "pi pi-check" },
  needs_more_information: { label: "Needs Info", severity: "info", icon: "pi pi-info-circle" },
};

const MENTOR_STATUS_CONFIG: Record<MentorRequestStatus, StatusConfig> = {
  open: { label: "Open", severity: "warning", icon: "pi pi-exclamation-circle" },
  closed: { label: "Closed", severity: "success", icon: "pi pi-check-circle" },
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const RequestDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const [activity, setActivity] = useState<ActivityDetailData | null>(null);
  const [notes, setNotes] = useState<ActivityNote[]>([]);
  const [historicalActivities, setHistoricalActivities] = useState<HistoricalActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  const isEvaluateRequest = location.pathname.startsWith(
    AdminAppRoutes.routes.champions.routes.evaluateRequests.route
  );
  const statusType = isEvaluateRequest ? "judgment_request" : "mentor_request";

  const { updateActivity: updateMentorActivity } = useMentorRequests();
  const { updateActivity: updateEvaluateActivity } = useEvaluateRequests();

  const backRoute = isEvaluateRequest
    ? AdminAppRoutes.routes.champions.routes.evaluateRequests.route
    : AdminAppRoutes.routes.champions.routes.mentorRequests.route;
  const backLabel = isEvaluateRequest ? "Back to Evaluation Requests" : "Back to Mentor Requests";

  // Breadcrumb: only set once we have the activity title
  useBreadcrumb(
    activity?.text
      ? {
          label: activity.text,
          route: `${backRoute}/${id ?? ""}`,
          display: "page",
        }
      : null
  );

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadActivity = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await fetchActivityDetail(id, statusType);
      const activityData = response.activity;

      // Derive requestStatus from the API response for both flows
      if (isEvaluateRequest) {
        activityData.requestStatus = activityData.status as EvaluateRequestStatus;
      } else {
        const historical = response.historicalActivities ?? [];
        const hasOpen = historical.some(act => act.status?.toLowerCase() === "open");
        activityData.requestStatus = hasOpen ? "open" : "closed";
      }

      setActivity(activityData);
      setNotes(response.notes ?? []);
      setHistoricalActivities(response.historicalActivities ?? []);
    } catch (err) {
      console.error("Failed to load activity:", err);
      setError("Failed to load activity");
      showToast({ severity: "error", summary: "Error", detail: "Failed to load activity" });
    } finally {
      setLoading(false);
    }
  };

  const loadActivityRef = useRef(loadActivity);
  loadActivityRef.current = loadActivity;

  useEffect(() => {
    void loadActivityRef.current();
  }, [id, statusType]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const getOpenActivityId = (): number | null => {
    if (!historicalActivities.length) return null;
    const open = historicalActivities.filter(a => a.status?.toLowerCase() === "open");
    if (!open.length) {
      return [...historicalActivities].sort((a, b) => b.id - a.id)[0]?.id ?? null;
    }
    return (
      open.sort((a, b) => {
        const at = new Date(a.createdAt ?? a.updatedAt ?? 0).getTime();
        const bt = new Date(b.createdAt ?? b.updatedAt ?? 0).getTime();
        return bt - at;
      })[0]?.id ?? null
    );
  };

  const getLastActivityTimestamp = (): string | null => {
    if (!historicalActivities.length) return activity?.createdAt ?? null;
    if (isEvaluateRequest) {
      const match = historicalActivities.find(a => a.id === activity?.id);
      if (match?.lastActivity) {
        return new Date(match.lastActivity * 1000).toISOString();
      }
    } else {
      const mostRecent = [...historicalActivities].sort(
        (a, b) => (b.lastActivity ?? 0) - (a.lastActivity ?? 0)
      )[0];
      if (mostRecent?.lastActivity) {
        return new Date(mostRecent.lastActivity * 1000).toISOString();
      }
    }
    return activity?.createdAt ?? null;
  };

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleAddNote = async (noteText: string) => {
    if (!activity) return;
    try {
      if (isEvaluateRequest) {
        await addEvaluateNote(activity.id, noteText);
      } else {
        const openId = getOpenActivityId();
        if (!openId) {
          showToast({ severity: "error", summary: "Error", detail: "No open activity found" });
          return;
        }
        await addMentorNote(openId, noteText);
      }
      await loadActivityRef.current();
      setIsAddingNote(false);
      showToast({ severity: "success", summary: "Success", detail: "Note added successfully" });
    } catch (err) {
      console.error("Failed to add note:", err);
      showToast({ severity: "error", summary: "Error", detail: "Failed to add note" });
      throw err; // rethrow so NoteDialog preserves the draft text
    }
  };

  const handleSetEvaluateStatus = async (status: EvaluateRequestStatus) => {
    if (!activity) return;
    try {
      await setEvaluateStatus(
        activity.id,
        status as "passed" | "failed" | "needs_more_information"
      );
      await loadActivityRef.current();
      updateEvaluateActivity(activity.id, { requestStatus: status });
      setShowStatusDialog(false);
      const STATUS_LABELS: Record<string, string> = {
        passed: "Passed",
        failed: "Failed",
        needs_more_information: "Needs More Information",
      };
      showToast({
        severity: "success",
        summary: "Success",
        detail: `Evaluation status set to ${STATUS_LABELS[status] ?? status}`,
      });
    } catch (err) {
      console.error("Failed to set evaluation status:", err);
      showToast({ severity: "error", summary: "Error", detail: "Failed to set evaluation status" });
    }
  };

  const handleCloseRequest = () => {
    confirmDialog({
      message: "Are you sure you want to close this mentor request?",
      header: "Close Mentor Request",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        if (!activity) return;
        try {
          const openId = getOpenActivityId();
          if (!openId) {
            showToast({ severity: "error", summary: "Error", detail: "No open activity found" });
            return;
          }
          await closeMentorRequest(openId);
          await loadActivityRef.current();
          updateMentorActivity(activity.id, { requestStatus: "closed" });
          showToast({
            severity: "success",
            summary: "Success",
            detail: "Mentor request closed successfully",
          });
        } catch (err) {
          console.error("Failed to close request:", err);
          showToast({
            severity: "error",
            summary: "Error",
            detail: "Failed to close mentor request",
          });
        }
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="max-w-[1200px] py-8">
        <Skeleton width="200px" height="36px" className="mb-4" />
        <Skeleton width="100%" height="500px" />
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="max-w-[1200px] py-8">
        <Button
          label={backLabel}
          icon="pi pi-arrow-left"
          text
          onClick={() => navigate(backRoute)}
          className="mb-4"
        />
        <Message severity="error" text={error ?? "Activity not found"} />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Status config
  // ---------------------------------------------------------------------------

  let config: StatusConfig;
  if (isEvaluateRequest) {
    config =
      EVALUATE_STATUS_CONFIG[(activity.requestStatus as EvaluateRequestStatus) ?? "submitted"] ??
      EVALUATE_STATUS_CONFIG["submitted"]!;
  } else {
    const rs = (activity.requestStatus ?? "open") as MentorRequestStatus;
    config = MENTOR_STATUS_CONFIG[rs] ?? MENTOR_STATUS_CONFIG["open"]!;
  }

  const lastActivityTs = getLastActivityTimestamp();
  const hasOpenActivity = historicalActivities.some(a => a.status?.toLowerCase() === "open");

  const header = (
    <div className={S.cardHeader}>
      <Tag value={config.label} severity={config.severity} icon={config.icon} />
      <div className="text-right">
        {activity.user && (
          <div className="body-sm">
            <i className="pi pi-user mr-1" />
            {activity.user.firstName} {activity.user.lastName}
          </div>
        )}
        <div className="body-xs text-muted">
          {lastActivityTs ? new Date(lastActivityTs).toLocaleString() : "N/A"}
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-between items-center flex-wrap gap-4">
      <div className="flex gap-2">
        {!isEvaluateRequest && hasOpenActivity && (
          <Button
            label="Close Request"
            icon="pi pi-times-circle"
            onClick={handleCloseRequest}
            severity="danger"
            outlined
          />
        )}
        {isEvaluateRequest && activity.requestStatus === "submitted" && (
          <Button
            label="Set Status"
            icon="pi pi-check-square"
            onClick={() => setShowStatusDialog(true)}
          />
        )}
      </div>
      <p className={`text-muted body-sm ${S.lastUpdated}`}>
        Last Activity:{" "}
        {activity.lastActivity ? new Date(activity.lastActivity * 1000).toLocaleString() : "N/A"}
      </p>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-[1200px] py-8">
      <Button
        label={backLabel}
        icon="pi pi-arrow-left"
        text
        onClick={() => navigate(backRoute)}
        className="mb-4"
      />

      <Card className={S.activityCard} header={header} footer={footer}>
        <div className="flex items-start justify-between gap-4 mb-8">
          <div className="flex align-items-center gap-2 mb-3">
            <h1 className="atlas-h3 m-0">{activity.text}</h1>
            <span className={S.points}>{activity.points} pts</span>
          </div>
        </div>

        {activity.description && (
          <div className="mb-4">
            <h2 className="atlas-h5 mb-2">Description</h2>
            <p className="text-color whitespace-pre-wrap">{activity.description}</p>
          </div>
        )}

        <div className="flex items-center gap-4 mb-2">
          <h2 className="atlas-h4 m-0">Activity log</h2>
          <Button
            icon="pi pi-plus"
            rounded
            size="small"
            onClick={() => setIsAddingNote(true)}
            aria-label="Add note"
            tooltip="Add note"
          />
        </div>
        <div className="mt-4">
          <ActivityMessages activityId={activity.id} initialNotes={notes} />
        </div>
      </Card>

      <NoteDialog
        visible={isAddingNote}
        onHide={() => setIsAddingNote(false)}
        onSubmit={handleAddNote}
      />

      <Dialog
        header="Set Evaluation Status"
        visible={showStatusDialog}
        onHide={() => setShowStatusDialog(false)}
        style={{ maxWidth: "450px" }}
      >
        <div className="flex flex-col gap-3">
          <p>Select the status for this evaluation request:</p>
          <Button
            label="Passed"
            icon="pi pi-check-circle"
            severity="success"
            onClick={() => void handleSetEvaluateStatus("passed")}
            className="w-full"
          />
          <Button
            label="Failed"
            icon="pi pi-times-circle"
            severity="danger"
            onClick={() => void handleSetEvaluateStatus("failed")}
            className="w-full"
          />
          <Button
            label="Needs More Information"
            icon="pi pi-info-circle"
            severity="warning"
            onClick={() => void handleSetEvaluateStatus("needs_more_information")}
            className="w-full"
          />
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default RequestDetail;
